import { Box, Typography, useMediaQuery } from '@mui/material';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useMeasure } from 'react-use';

import { COLORS } from '@/styles/tokens/colors';

import {
  GraphLayout,
  useHouseholdLoadingGraphLayout,
} from './hooks/useHouseholdLoadingGraphLayout';
import {
  COMPACT_GRAPH_SPACE,
  RENDER_CONFIG,
  WIDE_GRAPH_SPACE,
} from './householdLoadingGraph.config';
import {
  GraphEdge,
  GraphNode,
  GraphPoint,
} from './householdLoadingGraph.types';
import { HouseholdLoadingGraphFrame } from './HouseholdLoadingGraphFrame';
import {
  GraphComposition,
  householdLoadingCurveControl,
  householdLoadingCurveMidpoint,
  householdLoadingLabelGeometry,
  householdLoadingNodeRadius,
} from './householdLoadingGraphLayout';
import { HouseholdLoadingGraphNode } from './HouseholdLoadingGraphNode';

const {
  COMPACT_CONTAINER_MAX_WIDTH,
  NODE_EASE_MS,
  MAX_FRAME_MS,
  SATELLITE_SWAY,
  HUB_SWAY,
  SWAY_PERIOD_MS,
  PARTICLE_PERIOD_MS,
  PARTICLE_COUNT,
  SPOKE_OPACITY,
  RIM_OPACITY,
  EDGE_COLOR,
  NAME_COLOR,
  RELATIONSHIP_COLOR,
  KIND_COLORS,
} = RENDER_CONFIG;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function sway(id: string) {
  let value = 0;
  for (const character of id) {
    value = (value * 31 + character.charCodeAt(0)) >>> 0;
  }
  return {
    phase: (value % 1000) / 1000,
    period: SWAY_PERIOD_MS * (0.8 + ((value >>> 10) % 400) / 1000),
  };
}

type Registry<T extends Element> = Map<string, T>;

/** Point at `t` along the quadratic curve from `a` to `b` through `control`. */
function curvePoint(
  a: GraphPoint,
  control: GraphPoint,
  b: GraphPoint,
  t: number
) {
  const u = 1 - t;
  return {
    x: u * u * a.x + 2 * u * t * control.x + t * t * b.x,
    y: u * u * a.y + 2 * u * t * control.y + t * t * b.y,
  };
}

type Scene = { composition: GraphComposition; center: GraphPoint };

/**
 * Draws a calm composition of the household items found so far.
 *
 * Repulsion and spring forces place real items without fixed rings or groups.
 * A sparse selection of connections preserves the graph's connectivity.
 * Every frame positions nodes, labels and curve
 * ends from one set of eased coordinates, so curves stay attached while the
 * composition grows and sways. Reduced motion places everything directly.
 */
export function HouseholdLoadingGraph({
  nodes,
  edges,
  illustrative,
}: {
  nodes: readonly (GraphNode & { kind: string })[];
  edges: readonly GraphEdge[];
  illustrative: boolean;
}) {
  // Measure available space outside the capped frame to avoid breakpoint loops.
  const [containerRef, { width }] = useMeasure<HTMLDivElement>();
  const compact = width < COMPACT_CONTAINER_MAX_WIDTH;
  const { layout, failed } = useHouseholdLoadingGraphLayout({
    nodes,
    edges,
    compact,
    illustrative,
  });

  return (
    <Box
      ref={containerRef}
      sx={{ width: '100%', containerType: 'inline-size' }}
    >
      {layout ? (
        <HouseholdLoadingGraphPicture {...layout} />
      ) : (
        <HouseholdLoadingGraphFrame>
          {failed && (
            <Typography variant="body-s">Graph preview unavailable</Typography>
          )}
        </HouseholdLoadingGraphFrame>
      )}
    </Box>
  );
}

function HouseholdLoadingGraphPicture({
  nodes,
  edges,
  illustrative,
  compact,
  composition,
}: GraphLayout) {
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const space = compact ? COMPACT_GRAPH_SPACE : WIDE_GRAPH_SPACE;
  const center = { x: space.width / 2, y: space.height / 2 };
  const { points, roles, labels, edgeLabels } = composition;

  // Colors follow first appearance, so a kind keeps its color as items arrive.
  const [kindOrder, setKindOrder] = useState<string[]>([]);
  const newKinds = [...new Set(nodes.map((node) => node.kind))].filter(
    (kind) => !kindOrder.includes(kind)
  );
  if (newKinds.length) setKindOrder([...kindOrder, ...newKinds]);
  const nodesByID = new Map(nodes.map((node) => [node.id, node]));
  const colorOf = (id: string) =>
    KIND_COLORS[
      Math.max(kindOrder.indexOf(nodesByID.get(id)?.kind ?? ''), 0) %
        KIND_COLORS.length
    ]!;

  const shownNodes = nodes.filter((node) => points.has(node.id));
  const spokes = composition.edges.filter((edge) => edge.curve === 'spoke');
  // Particles travel along the spokes that lead to named items.
  const particleEdges = spokes
    .filter((edge) => roles.get(edge.targetID) === 'primary')
    .slice(0, PARTICLE_COUNT);

  const elements = useRef({
    nodes: new Map<string, SVGGElement>(),
    labels: new Map<string, SVGGElement>(),
    edges: new Map<string, SVGPathElement>(),
    edgeLabels: new Map<string, SVGGElement>(),
    particles: new Map<string, SVGCircleElement>(),
  });
  const shown = useRef(new Map<string, GraphPoint>());
  const scene = useRef<Scene>({ composition, center });

  const paint = useCallback((time: number, swaying: boolean) => {
    const { composition: scenePicture, center: sceneCenter } = scene.current;
    const positions = new Map<string, GraphPoint>();
    for (const id of scenePicture.points.keys()) {
      const base = shown.current.get(id);
      if (!base) continue;
      const { phase, period } = sway(id);
      const angle = (time / period + phase) * Math.PI * 2;
      const amplitude =
        scenePicture.roles.get(id) === 'hub' ? HUB_SWAY : SATELLITE_SWAY;
      positions.set(id, {
        x: base.x + (swaying ? Math.sin(angle) * amplitude : 0),
        y: base.y + (swaying ? Math.cos(angle * 1.3) * amplitude : 0),
      });
    }
    const place = (element: SVGGElement | undefined, point?: GraphPoint) => {
      if (point) {
        element?.setAttribute('transform', `translate(${point.x} ${point.y})`);
      }
    };
    for (const [id, point] of positions) {
      place(elements.current.nodes.get(id), point);
      place(elements.current.labels.get(id), point);
    }
    const controls = new Map<string, GraphPoint>();
    for (const edge of scenePicture.edges) {
      const a = positions.get(edge.sourceID);
      const b = positions.get(edge.targetID);
      if (!a || !b) continue;
      const control = householdLoadingCurveControl(
        a,
        b,
        edge.curve,
        sceneCenter
      );
      controls.set(edge.id, control);
      elements.current.edges
        .get(edge.id)
        ?.setAttribute(
          'd',
          `M ${a.x} ${a.y} Q ${control.x} ${control.y} ${b.x} ${b.y}`
        );
      place(
        elements.current.edgeLabels.get(edge.id),
        householdLoadingCurveMidpoint(a, b, control)
      );
    }
    scenePicture.edges
      .filter((edge) => edge.curve === 'spoke')
      .filter((edge) => scenePicture.roles.get(edge.targetID) === 'primary')
      .slice(0, PARTICLE_COUNT)
      .forEach((edge, index) => {
        const dot = elements.current.particles.get(edge.id);
        const a = positions.get(edge.sourceID);
        const b = positions.get(edge.targetID);
        const control = controls.get(edge.id);
        if (!dot || !a || !b || !control) return;
        const progress =
          (time / PARTICLE_PERIOD_MS + index / PARTICLE_COUNT) % 1;
        // Alternate directions so the motion reads as exchange, not a flow.
        const along = index % 2 === 0 ? progress : 1 - progress;
        const point = curvePoint(a, control, b, along);
        dot.setAttribute('cx', `${point.x}`);
        dot.setAttribute('cy', `${point.y}`);
        dot.setAttribute('opacity', `${Math.sin(progress * Math.PI) * 0.8}`);
      });
  }, []);

  // Runs before paint, so new elements never show at the SVG origin.
  useLayoutEffect(() => {
    const known = shown.current;
    for (const id of [...known.keys()]) if (!points.has(id)) known.delete(id);
    for (const [id, target] of points) {
      if (known.has(id)) continue;
      // A connected new item emerges from the item it connects to. An
      // unconnected one appears where it belongs.
      const anchorID = composition.edges
        .map((edge) => {
          if (edge.sourceID === id) return edge.targetID;
          if (edge.targetID === id) return edge.sourceID;
          return undefined;
        })
        .find((other) => other !== undefined && known.has(other));
      const start = reducedMotion || !anchorID ? target : known.get(anchorID)!;
      known.set(id, { ...start });
    }
    if (reducedMotion) {
      for (const [id, target] of points) known.set(id, { ...target });
    }
    scene.current = { composition, center };
    paint(performance.now(), false);
  });

  useEffect(() => {
    if (reducedMotion) return;
    let frame = 0;
    let last = performance.now();
    const step = (now: number) => {
      const ease =
        1 - Math.exp(-Math.min(now - last, MAX_FRAME_MS) / NODE_EASE_MS);
      last = now;
      for (const [id, target] of scene.current.composition.points) {
        const point = shown.current.get(id);
        if (!point) continue;
        point.x += (target.x - point.x) * ease;
        point.y += (target.y - point.y) * ease;
      }
      paint(now, true);
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [paint, reducedMotion]);

  const register = <T extends Element>(
    registry: Registry<T>,
    id: string,
    element: T | null
  ) => {
    if (element) registry.set(id, element);
    else registry.delete(id);
  };
  const { config } = composition;

  return (
    <HouseholdLoadingGraphFrame>
      <svg
        viewBox={`0 0 ${space.width} ${space.height}`}
        role="img"
        aria-label={
          illustrative
            ? 'Illustration of a graph being assembled'
            : `Preview of extracted household information: ${nodes.length} items and ${edges.length} relationships found so far`
        }
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          pointerEvents: 'none',
        }}
      >
        <g>
          {composition.edges.map((edge) => (
            <path
              key={edge.id}
              ref={(element) =>
                register(elements.current.edges, edge.id, element)
              }
              className={
                edge.curve === 'rim'
                  ? 'graph-edge graph-cross-edge'
                  : 'graph-edge'
              }
              pathLength={edge.curve === 'rim' ? undefined : 1}
              fill="none"
              stroke={EDGE_COLOR}
              strokeOpacity={
                edge.curve === 'spoke' ? SPOKE_OPACITY : RIM_OPACITY
              }
              strokeWidth={0.85}
              strokeLinecap="round"
            />
          ))}
          {particleEdges.map((edge) => (
            <circle
              key={edge.id}
              ref={(element) =>
                register(elements.current.particles, edge.id, element)
              }
              className="graph-particle"
              r={1.5}
              fill={colorOf(edge.targetID)}
              opacity={0}
            />
          ))}
          {shownNodes.map((node, index) => {
            const role = roles.get(node.id)!;
            const color = colorOf(node.id);
            return (
              <g
                key={node.id}
                ref={(element) =>
                  register(elements.current.nodes, node.id, element)
                }
                className="graph-node"
              >
                <HouseholdLoadingGraphNode
                  role={role}
                  color={color}
                  index={index}
                />
              </g>
            );
          })}
          {/* Names are drawn last so nodes and curves cannot cover them. */}
          {shownNodes.flatMap((node) => {
            const label = labels.get(node.id);
            if (!label) return [];
            const isHub = roles.get(node.id) === 'hub';
            const fontSize = config.labelFontSize + (isHub ? 1 : 0);
            const geometry = householdLoadingLabelGeometry({
              side: label.side,
              lines: label.lines,
              radius: householdLoadingNodeRadius(roles.get(node.id)!),
              fontSize,
            });
            return [
              <g
                key={node.id}
                ref={(element) =>
                  register(elements.current.labels, node.id, element)
                }
              >
                <text
                  className="graph-label-reveal"
                  textAnchor={geometry.textAnchor}
                  fill={NAME_COLOR}
                  stroke={COLORS.PRIMITIVES.WHITE}
                  strokeOpacity={0.65}
                  strokeWidth={3}
                  strokeLinejoin="round"
                  paintOrder="stroke"
                  fontSize={fontSize}
                  fontWeight={isHub ? 600 : 500}
                >
                  {label.lines.map((line, lineIndex) => (
                    <tspan
                      key={lineIndex}
                      x={geometry.x}
                      y={geometry.baseline + lineIndex * geometry.lineHeight}
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
              </g>,
            ];
          })}
          {composition.edges.flatMap((edge) => {
            const label = edgeLabels.get(edge.id);
            if (!label) return [];
            return [
              <g
                key={edge.id}
                ref={(element) =>
                  register(elements.current.edgeLabels, edge.id, element)
                }
              >
                <text
                  className="graph-label-reveal"
                  textAnchor="middle"
                  y={config.edgeLabelFontSize * 0.35}
                  fill={RELATIONSHIP_COLOR}
                  stroke={COLORS.PRIMITIVES.WHITE}
                  strokeWidth={4}
                  strokeLinejoin="round"
                  paintOrder="stroke"
                  fontSize={config.edgeLabelFontSize}
                  fontStyle="italic"
                >
                  {label.lines.map((line, lineIndex) => (
                    <tspan
                      key={lineIndex}
                      x={0}
                      dy={lineIndex === 0 ? 0 : '1.2em'}
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
              </g>,
            ];
          })}
        </g>
      </svg>
    </HouseholdLoadingGraphFrame>
  );
}
